import React from 'react';
import { Play, FileText, BookOpen, Info, Shield, Library, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MegaMenuItem {
  icon: React.ElementType;
  label: string;
  helper: string;
  route?: string;
  action?: 'demo' | 'navigate';
  iconAccent?: boolean;
}

const GROUP_START: MegaMenuItem[] = [
  { icon: Play, label: 'Try Interactive Demo', helper: 'See Verithos in action (2–3 min)', action: 'demo', iconAccent: true },
  { icon: FileText, label: 'View Sample Report', helper: 'What export-ready output looks like', action: 'navigate', route: '/sample-report' },
];

const GROUP_UNDERSTAND: MegaMenuItem[] = [
  { icon: Info, label: 'What is Verithos?', helper: 'Definition, methodology, and boundaries', action: 'navigate', route: '/what-is-verithos' },
  { icon: FileText, label: 'Verithos vs LLMs', helper: 'Why workflow + traceability matters', action: 'navigate', route: '/verithos-vs-llms' },
  { icon: FileText, label: 'QDAS Comparison 2026', helper: 'vs NVivo, ATLAS.ti, MAXQDA, Dedoose', action: 'navigate', route: '/verithos-vs-nvivo-atlasti-maxqda-dedoose' },
  { icon: Library, label: 'Evidence Brief', helper: 'Whitepaper on method, governance, and limits', action: 'navigate', route: '/whitepaper' },
];

const GROUP_USE: MegaMenuItem[] = [
  { icon: BookOpen, label: 'How to Cite', helper: 'Disclosure guidance for researchers', action: 'navigate', route: '/how-to-cite' },
  { icon: Shield, label: 'Privacy & Security', helper: 'How Verithos handles data', action: 'navigate', route: '/privacy-policy' },
];

interface ResourcesMegaMenuProps {
  isDarkContext: boolean;
  onItemClick: (item: MegaMenuItem) => void;
  onViewAll: () => void;
}

export const ResourcesMegaMenu = React.forwardRef<HTMLDivElement, ResourcesMegaMenuProps>(
  function ResourcesMegaMenu({ isDarkContext, onItemClick, onViewAll }, ref) {
  const renderItem = (item: MegaMenuItem) => {
    const Icon = item.icon;
    return (
      <button
        key={item.label}
        onClick={() => onItemClick(item)}
        className={cn(
          "group flex items-start gap-3 rounded-lg px-3 py-3 text-left transition-all duration-150 w-full",
          isDarkContext
            ? "hover:bg-white/[0.06]"
            : "hover:bg-muted/50"
        )}
      >
        <div className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
          item.iconAccent
            ? isDarkContext ? 'bg-amber-400/10 text-amber-400' : 'bg-primary/10 text-primary'
            : isDarkContext ? 'bg-white/[0.06] text-white/50' : 'bg-muted text-muted-foreground'
        )}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <span className={cn(
            "text-sm font-medium",
            isDarkContext ? "text-white/90" : "text-foreground"
          )}>{item.label}</span>
          <p className={cn(
            "mt-0.5 text-[11px] leading-relaxed",
            isDarkContext ? "text-white/40" : "text-muted-foreground"
          )}>{item.helper}</p>
        </div>
      </button>
    );
  };

  const groupLabelClass = cn(
    "block px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider",
    isDarkContext ? "text-white/30" : "text-muted-foreground/50"
  );

  return (
    <div ref={ref} className={cn(
      "border-b shadow-2xl",
      isDarkContext
        ? "bg-[hsl(230,30%,13%)] border-white/[0.06]"
        : "bg-background/[0.98] backdrop-blur-xl border-border/30"
    )}>
      <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-[1400px] mx-auto px-6 2xl:px-8 py-6 2xl:py-8">
        <div className="grid grid-cols-3 gap-6 2xl:gap-10">
          <div>
            <span className={groupLabelClass}>Start here</span>
            <div className="space-y-0.5">{GROUP_START.map(renderItem)}</div>
          </div>
          <div>
            <span className={groupLabelClass}>Understand Verithos</span>
            <div className="space-y-0.5">{GROUP_UNDERSTAND.map(renderItem)}</div>
          </div>
          <div>
            <span className={groupLabelClass}>Use it properly</span>
            <div className="space-y-0.5">{GROUP_USE.map(renderItem)}</div>
          </div>
        </div>

        <div className={cn(
          "mt-5 pt-4 border-t flex items-center justify-end",
          isDarkContext ? "border-white/[0.06]" : "border-border/30"
        )}>
          <button
            onClick={onViewAll}
            className={cn(
              "flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 transition-colors",
              isDarkContext
                ? "text-amber-400/80 hover:text-amber-400 hover:bg-white/[0.04]"
                : "text-primary hover:bg-primary/5"
            )}
          >
            View all resources
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
});
